/**
 * Groups players into tiers: First Team, Rotation, Academy.
 *
 * Tier assignment uses the `isAcademy` flag and `readinessTag` from the
 * backend. When the backend fields are not yet available, falls back to
 * a heuristic based on position presence.
 * @module features/squad-planning/utils/groupPlayersByTier
 */

import { TIER_ORDER } from "../constants";
import type { SquadPlayer, TierName, TieredPlayers } from "../types";

/**
 * Derives the tier name for a single player.
 *
 * Logic:
 * - `isAcademy === true` → "Academy"
 * - `readinessTag === "Rotation"` → "Rotation"
 * - otherwise → "First Team"
 * @param player - The player to classify
 * @returns The tier name
 */
export function deriveTierName(player: SquadPlayer): TierName {
  if (player.tierName === "Targets") return "Targets";
  if (player.isAcademy) return "Academy";
  if (player.readinessTag === "Rotation") return "Rotation";
  return "First Team";
}

/**
 * Groups an array of players into the three squad tiers.
 * @param players - All players for the selected position
 * @returns Players grouped by tier, preserving the original order within each tier
 */
export function groupPlayersByTier(
  players: readonly SquadPlayer[]
): TieredPlayers {
  const grouped = players.reduce<
    Readonly<Record<TierName, readonly SquadPlayer[]>>
  >(
    (acc, player) => {
      const tier = deriveTierName(player);
      return { ...acc, [tier]: [...acc[tier], player] };
    },
    { "First Team": [], Rotation: [], Academy: [], Targets: [] }
  );

  return Object.fromEntries(
    TIER_ORDER.map(tier => [tier, grouped[tier]])
  ) as TieredPlayers;
}
